import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import Select from "react-select";

import {
  showNotificationWithTimeout,
  saveLaborRule,
} from "../../../Redux/actions";
import ErrorContainer from "../../Components/ErrorContainer";
import ModelContainer from "../../Components/ModelContainer";
import { capitalizeFirstLetter, checkNull } from "../../../helpers";

const schema = yup
  .object({
    id: yup.string(),
    rule_name: yup.string().required(),
    employment_status_id: yup.string().required(),
    employee_function_id: yup.string().required(),
    employee_category_id: yup.string().required(),
    employee_category: yup.string().when("employee_category_id", {
      is: "1",
      then: yup.string().required(),
    }),
    employee_select: yup.array().required().min(1),
    overtime_accrual_type_id: yup.string().required(),
    accrue_after: yup.number().when("overtime_accrual_type_id", {
      is: (accrualTypeId) => accrualTypeId != 3,
      then: yup.number().positive().integer().required(),
    }),
    overtime_multiplier: yup.number().positive().integer().required(),
    leave_types_included: yup.array().when("overtime_accrual_type_id", {
      is: "2",
      then: yup.array().required().min(1),
    }),
  })
  .required();
const OvertimeRule = ({ openDeleteRuleModal }) => {
  const dispatch = useDispatch();
  const {
    account,
    employmentStatuses,
    employeeFunctions,
    emp_category,
    employees,
    overtimeAccrualTypes,
    leaveTypes,
    overtime_rules,
  } = useSelector((state) => state.authReducer.setup_wizard_details);
  const initialForm = {
    id: "",
    rule_name: "",
    employment_status_id: "",
    employee_function_id: "",
    employee_category_id: "",
    employee_category: "",
    employee_select: [],
    overtime_accrual_type_id: "",
    accrue_after: "",
    overtime_multiplier: "",
    leave_types_included: [],
  };
  const {
    register,
    formState: { errors },
    handleSubmit,
    control,
    setValue,
    reset,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: initialForm,
    mode: "all",
    reValidateMode: "onChange",
  });
  const [ruleModal, setRuleModal] = useState(false);
  const [templateModal, setTemplateModal] = useState(false);
  const [templateId, setTemplateId] = useState("");
  const [empsArr, setEmpsArr] = useState([]);
  const [leaveTypesArr, setLeaveTypesArr] = useState([]);
  const [inputForm, setInputForm] = useState(initialForm);

  const changeInputHandler = (input, val) => {
    // employee all selected option
    if (
      input == "employee_select" &&
      val.length > 0 &&
      val.findIndex((p) => p.value == "all") > -1
    ) {
      val = addEmpToArray(true);
    } else if (
      input == "employee_select" &&
      inputForm?.employee_select?.length > 0 &&
      inputForm?.employee_select.findIndex((p) => p.value == "all") > -1 &&
      val.findIndex((p) => p.value == "all") == -1
    ) {
      val = [];
    }
    // employee all selected option END

    // leave_types_included all selected option
    if (
      input == "leave_types_included" &&
      val.length > 0 &&
      val.findIndex((p) => p.value == "all") > -1
    ) {
      val = addEmpToArray(true);
    } else if (
      input == "leave_types_included" &&
      inputForm?.leave_types_included?.length > 0 &&
      inputForm?.leave_types_included.findIndex((p) => p.value == "all") > -1 &&
      val.findIndex((p) => p.value == "all") == -1
    ) {
      val = [];
    }
    // leave_types_included all selected option END

    setValue(input, val);
    setInputForm((prevState) => ({ ...prevState, [input]: val }));
  };

  const initialModalState = () => {
    setInputForm(initialForm);
    reset(initialForm);
  };

  const closeTemplateModal = useCallback(
    (value) => {
      setTemplateModal(value);
    },
    [ruleModal]
  );

  const closeModal = useCallback(
    (value) => {
      setRuleModal(value);
      initialModalState();
    },
    [ruleModal]
  );

  const addEmpToArray = (isDisable = false) => {
    let newEmpArr = [];
    Object.keys(employees).map((emp) => {
      const newEmpObj = { value: emp, label: employees[emp] };
      if (emp !== "all" && isDisable) {
        newEmpObj.isDisabled = true;
      }
      newEmpArr.push(newEmpObj);
    });
    return newEmpArr;
  };

  const addLeaveTypesToArray = (isDisable = false) => {
    let newLeaveArr = [{ value: "all", label: "All" }];
    Object.keys(leaveTypes).map((leave) => {
      const newLeaveObj = { value: leave, label: leaveTypes[leave] };
      if (leave !== "all" && isDisable) {
        newLeaveObj.isDisabled = true;
      }
      newLeaveArr.push(newLeaveObj);
    });
    return newLeaveArr;
  };

  useEffect(() => {
    if (employees && employees !== null) {
      setEmpsArr(addEmpToArray());
    }
    if (leaveTypes && leaveTypes !== null) {
      setLeaveTypesArr(addLeaveTypesToArray());
    }
  }, [employees, leaveTypes]);

  const editRuleHandler = (editRule, isTemplate = false) => {
    if (isTemplate) closeTemplateModal(false);
    closeModal(true);
    const editValues = {
      id: isTemplate ? "" : checkNull(editRule?.id),
      rule_name: isTemplate ? "" : checkNull(editRule?.rule_name),
      employment_status_id: checkNull(editRule?.employment_status_id),
      employee_function_id: checkNull(editRule?.employee_function_id),
      employee_category_id: checkNull(editRule?.employee_category_id),
      employee_category: "",
      employee_select: [],
      leave_types_included: [],
      overtime_accrual_type_id: checkNull(editRule?.overtime_accrual_type_id),
      accrue_after: checkNull(editRule?.accrue_after),
      overtime_multiplier: checkNull(editRule?.overtime_multiplier),
    };
    if (
      editRule?.employee_select &&
      checkNull(editRule?.employee_select, false)
    ) {
      let newEmpArr = [];
      if (editRule?.employee_select.split(",").length > 0) {
        editRule?.employee_select.split(",").map((emp) => {
          if (employees?.[emp]) {
            newEmpArr.push({ value: emp, label: employees[emp] });
          }
        });
      }
      editValues.employee_select = newEmpArr;
    }
    if (
      editRule?.leave_types_included &&
      checkNull(editRule?.leave_types_included, false)
    ) {
      let newLeaveTypeArr = [];
      if (editRule?.leave_types_included.split(",").length > 0) {
        editRule?.leave_types_included.split(",").map((leave) => {
          if (leaveTypes?.[leave]) {
            newLeaveTypeArr.push({ value: leave, label: leaveTypes[leave] });
          }
        });
      }
      editValues.leave_types_included = newLeaveTypeArr;
    }
    setInputForm(editValues);
    reset(editValues);
  };

  const templateSubmitHandler = () => {
    if (
      templateId &&
      overtime_rules &&
      overtime_rules.length > 0 &&
      overtime_rules.findIndex((p) => p.id == templateId) > -1
    ) {
      const templateIndex = overtime_rules.findIndex((p) => p.id == templateId);
      editRuleHandler(overtime_rules?.[templateIndex], true);
    } else {
      dispatch(
        showNotificationWithTimeout("Please select valid template!", "error")
      );
    }
  };

  const submitForm = (data) => {
    console.log("submitForm", data);
    data.employee_select = data.employee_select.map((emp) => emp.value);
    data.leave_types_included = data.leave_types_included
      .filter((p) => p.value !== "all")
      .map((leave) => leave.value);
    dispatch(saveLaborRule(data, "overtime"));
    closeModal(false);
  };

  return (
    <>
      <h4 className="sm-hd light mt-5"> Overtime Rules </h4>

      <div class="table-responsive inner-space screen-scroll">
        <table class="table checklist-table remove-space inventory_table">
          <thead>
            <tr>
              <th> Rule Name &amp; Type </th>
              <th>Employment Status</th>
              <th>Function</th>
              <th>Category</th>
              <th>Employee Name</th>
              <th>Accrue After</th>
              <th>Multiplier</th>
              <th></th>
              <th className="action-td">Actions</th>
            </tr>
          </thead>
          <tbody>
            {overtime_rules &&
              overtime_rules.length > 0 &&
              overtime_rules.map((rule) => {
                return (
                  <tr>
                    <td>
                      <b>{rule?.rule_name}</b> <br />{" "}
                      {rule?.overtime_accrual_type?.name}
                    </td>
                    <td>
                      {rule?.employment_status_id == "all"
                        ? "All"
                        : rule?.employment_status?.name ?? "-"}
                    </td>
                    <td>All</td>
                    <td>
                      {" "}
                      {rule?.employee_category_id == "all"
                        ? "All"
                        : rule?.employee_category?.name ?? "-"}{" "}
                    </td>
                    <td>
                      {" "}
                      {checkNull(rule?.employee_select_name)
                        ? capitalizeFirstLetter(rule?.employee_select_name)
                        : "-"}{" "}
                    </td>
                    <td> {rule?.accrue_after} </td>
                    <td> {rule?.overtime_multiplier} </td>
                    <td> {rule?.leave_types_included_name ?? "None"} </td>
                    <td class="action-td">
                      <div class="d-flex">
                        {rule?.parent_id == 0 && (
                          <>
                            <a
                              className="open_lcr_edit circle_icon"
                              onClick={() => editRuleHandler(rule)}
                            >
                              <i className="iconSprites edit-icon"></i>
                            </a>
                            <a
                              className="deleterulesleave circle_icon red"
                              onClick={() =>
                                openDeleteRuleModal({
                                  id: rule?.id,
                                  rule: "overtime",
                                })
                              }
                            >
                              <i className="iconSprites delete-icon"></i>
                            </a>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
      <div className="my-3">
        <a
          id=""
          class="btn green-btn-revert add-plus open_lcr "
          onClick={() => closeModal(true)}
        >
          <i class="i-icon"> </i> Add Rule{" "}
        </a>
        <a
          id=""
          onClick={() => closeTemplateModal(true)}
          class="btn bordered-btn open_lcr_template "
        >
          {" "}
          Add From Template{" "}
        </a>
      </div>
      <ModelContainer
        title={`${inputForm?.id !== "" ? "Edit" : "Add"} Overtime Rules`}
        open={ruleModal}
        closeModal={closeModal}
        submitModal={handleSubmit(submitForm)}
        is_loading={false}
      >
        <form>
          {console.log("Errors", errors)}
          {console.log("inputForm", inputForm)}
          <div className="form-list popup-modal">
            <ul className="px-0">
              <li>
                <label className="required"> Rule Name </label>
                <div className="input-group mb10">
                  <input
                    {...register("rule_name")}
                    className="required"
                    placeholder="Rule Name"
                    type="text"
                    onChange={(event) =>
                      changeInputHandler("rule_name", event?.target?.value)
                    }
                    value={inputForm?.rule_name}
                  />
                  <ErrorContainer errors={errors} name="rule_name" />
                </div>
              </li>
              <li>
                <label className="required"> Employee Status </label>
                <div className="input-wrap select-div">
                  <select
                    {...register("employment_status_id")}
                    name="employment_status_id"
                    className="required changing"
                    onChange={(event) =>
                      changeInputHandler(
                        "employment_status_id",
                        event?.target?.value
                      )
                    }
                    disabled={inputForm?.id !== ""}
                    value={inputForm?.employment_status_id}
                  >
                    <option value=""> Select Employment Status </option>
                    {employmentStatuses &&
                      employmentStatuses !== null > 0 &&
                      Object.keys(employmentStatuses).map((index) => {
                        return (
                          <option value={index} key={index}>
                            {" "}
                            {employmentStatuses?.[index]}{" "}
                          </option>
                        );
                      })}
                  </select>
                  <ErrorContainer errors={errors} name="employment_status_id" />
                </div>
              </li>
              <li>
                <label className="required"> Employee Function </label>
                <div className="input-wrap select-div">
                  <select
                    {...register("employee_function_id")}
                    name="employee_function_id"
                    className="required changing"
                    onChange={(event) =>
                      changeInputHandler(
                        "employee_function_id",
                        event?.target?.value
                      )
                    }
                    disabled={inputForm?.id !== ""}
                    value={inputForm?.employee_function_id}
                  >
                    <option value=""> Select Employee Function </option>
                    {employeeFunctions &&
                      employeeFunctions !== null > 0 &&
                      Object.keys(employeeFunctions).map((index) => {
                        return (
                          <option value={index} key={index}>
                            {" "}
                            {employeeFunctions?.[index]}{" "}
                          </option>
                        );
                      })}
                  </select>
                  <ErrorContainer errors={errors} name="employee_function_id" />
                </div>
              </li>
              <li>
                <label className="required"> Employee Category </label>
                <div className="input-wrap select-div">
                  <select
                    {...register("employee_category_id")}
                    name="employee_category_id"
                    className="required changing"
                    onChange={(event) =>
                      changeInputHandler(
                        "employee_category_id",
                        event?.target?.value
                      )
                    }
                    disabled={inputForm?.id !== ""}
                    value={inputForm?.employee_category_id}
                  >
                    <option value=""> Select Employee Category </option>
                    {emp_category &&
                      emp_category !== null > 0 &&
                      Object.keys(emp_category).map((index) => {
                        return (
                          <option value={index} key={index}>
                            {" "}
                            {emp_category?.[index]}{" "}
                          </option>
                        );
                      })}
                  </select>
                  <ErrorContainer errors={errors} name="employee_category_id" />
                </div>
              </li>
              {inputForm?.employee_category_id == 1 && (
                <li>
                  <label className="required"> Employee Category </label>
                  <div className="input-group mb10">
                    <input
                      {...register("employee_category")}
                      className="required"
                      type="text"
                      onChange={(event) =>
                        changeInputHandler(
                          "employee_category",
                          event?.target?.value
                        )
                      }
                      value={inputForm?.employee_category}
                    />
                    <ErrorContainer errors={errors} name="employee_category" />
                  </div>
                </li>
              )}

              <li>
                <label className="required"> Employee Select </label>
                <div className="px-0">
                  <Select
                    className="w-100"
                    {...register("employee_select")}
                    value={inputForm?.employee_select}
                    onChange={(values) =>
                      changeInputHandler("employee_select", values)
                    }
                    options={empsArr}
                    isMulti
                    isDisabled={inputForm?.id !== ""}
                  />
                  <ErrorContainer errors={errors} name="employee_select" />
                </div>
              </li>
              <li>
                <label className="required"> Overtime Accrual Type </label>
                <div className="input-wrap select-div">
                  <select
                    {...register("overtime_accrual_type_id")}
                    name="overtime_accrual_type_id"
                    className="required changing"
                    onChange={(event) =>
                      changeInputHandler(
                        "overtime_accrual_type_id",
                        event?.target?.value
                      )
                    }
                    disabled={inputForm?.id !== ""}
                    value={inputForm?.overtime_accrual_type_id}
                  >
                    <option value=""> Select Overtime Accrual Type </option>
                    {overtimeAccrualTypes &&
                      overtimeAccrualTypes !== null > 0 &&
                      Object.keys(overtimeAccrualTypes).map((index) => {
                        return (
                          <option value={index} key={index}>
                            {" "}
                            {overtimeAccrualTypes?.[index]}{" "}
                          </option>
                        );
                      })}
                  </select>
                  <ErrorContainer
                    errors={errors}
                    name="overtime_accrual_type_id"
                  />
                </div>
              </li>
              {inputForm?.overtime_accrual_type_id != 3 && (
                <li>
                  <label className="required"> Accrue After </label>
                  <div className="input-group mb10">
                    <input
                      {...register("accrue_after")}
                      className="required"
                      placeholder="Num of Hours"
                      type="text"
                      onChange={(event) =>
                        changeInputHandler("accrue_after", event?.target?.value)
                      }
                      value={inputForm?.accrue_after}
                    />
                    <ErrorContainer errors={errors} name="accrue_after" />
                  </div>
                </li>
              )}
              <li>
                <label className="required"> Overtime Multiplier </label>
                <div className="input-group mb10">
                  <input
                    {...register("overtime_multiplier")}
                    className="required"
                    placeholder="Overtime Multiplier"
                    type="text"
                    onChange={(event) =>
                      changeInputHandler(
                        "overtime_multiplier",
                        event?.target?.value
                      )
                    }
                    value={inputForm?.overtime_multiplier}
                  />
                  <ErrorContainer errors={errors} name="overtime_multiplier" />
                </div>
              </li>
              {inputForm?.overtime_accrual_type_id == 2 && (
                <li>
                  <label className="required">
                    {" "}
                    Leave Types Included in Accrued Hours{" "}
                  </label>
                  <div className="px-0">
                    <Select
                      className="w-100"
                      {...register("leave_types_included")}
                      value={inputForm?.leave_types_included}
                      onChange={(values) =>
                        changeInputHandler("leave_types_included", values)
                      }
                      options={leaveTypesArr}
                      isMulti
                    />
                    <ErrorContainer
                      errors={errors}
                      name="leave_types_included"
                    />
                  </div>
                </li>
              )}
            </ul>
          </div>
        </form>
      </ModelContainer>
      {/* From Template section */}
      <ModelContainer
        title={`"Add Leave/Claim Rules`}
        open={templateModal}
        closeModal={closeTemplateModal}
        submitModal={templateSubmitHandler}
        is_loading={false}
      >
        <form>
          <div className="form-list popup-modal">
            <ul className="px-0">
              <li>
                <label className="required"> Select Templates </label>
                <div className="input-wrap select-div">
                  <select
                    name="template_id"
                    className="required changing"
                    onChange={(event) => setTemplateId(event?.target?.value)}
                    value={templateId}
                  >
                    <option value=""> Select </option>
                    {overtime_rules &&
                      overtime_rules.length > 0 &&
                      overtime_rules.map((leave) => {
                        return (
                          <option value={leave?.id} key={leave?.id}>
                            {leave?.rule_name}
                          </option>
                        );
                      })}
                  </select>
                </div>
              </li>
            </ul>
          </div>
        </form>
      </ModelContainer>
      {/* From Template section END */}
    </>
  );
};
export default OvertimeRule;
